import React from "react";
import {fromJS} from "immutable";
import {Api} from "../../repository/api";
import {MenuOptionType} from "../../repository/model/menuItem";
import {useParams} from "react-router";

interface WebsiteMenuItemOptionsContextType {
  queries: any,
  data: any,
  website: any,
  loading: boolean,
  onBrandChanged: (brand?: any) => void,
  deleteMenuItemOption: (itemId: any) => void,
  onCreateMenuItemOption: (option: MenuOptionType) => Promise<Boolean>,
  onEditMenuItemOption: (optionId: string, option: MenuOptionType) => void,
  onNextPage: () => void,
  onPreviousPage: () => void,
  onFirstPage: () => void,
}

let WebsiteMenuItemOptionsContext = React.createContext<WebsiteMenuItemOptionsContextType>(null!);

const useWebsiteMenuItemOptions = () => {
  return React.useContext(WebsiteMenuItemOptionsContext);
};

function WebsiteMenuItemOptionsProvider({ children }: { children: React.ReactNode }) {

  const [queries, setQueries] = React.useState<any>(fromJS({}));
  const [data, setData] = React.useState<any>(fromJS([]))
  const [meta, setMeta] = React.useState<any>()
  const [loading, setLoading] = React.useState(false);
  const [website, setWebsite] = React.useState<any>(fromJS({}))
  const {websiteId} = useParams()

  const _fetchWebsite = async () => {
    try {
      if (websiteId) {
        setLoading(true)
        const res = await Api.brand.fetchBrand(websiteId)
        setLoading(false)
        setWebsite(fromJS(res.data.data))
      }
    } catch (e: any) {
      console.log(e)
      window.alert(
        e?.response?.data?.error?.message ??
        'Có lỗi xảy ra trong quá trình xử lý. Vui lòng thử lại sau'
      )
      return
    }
  }

  const _fetchMenuItems = async() => {
    try {
      const rs: any = await Api.menu.fetchMenuOptionList({
        brandFields: "_id,name",
        limit: 20,
        page: queries.get('page'),
        brand: queries.getIn(['brand', '_id']),
        majorBrand: websiteId,
        search: queries.get('search')
      })
      setMeta(rs.data.meta)
      return fromJS(rs.data.data)
    } catch (e: any) {
      window.alert(
        e?.response?.data?.error?.message ??
        'Có lỗi xảy ra trong quá trình xử lý. Vui lòng thử lại sau'
      )
      return fromJS([])
    }
  }

  const _refresh = async () => {
    if (loading) return
    setLoading(true)

    const list: any = await _fetchMenuItems()
    setData(list)

    setLoading(false)
  }

  const _initData = async () => {
    _initQueries()
  }

  const _initQueries = async () => {
    setQueries(queries.updateIn(['page'], () => 1))
  }

  const onBrandChanged = (brand?: any) => {
    setQueries(
      queries.updateIn(['brand'], () => brand)
        .updateIn(['page'], () => 1)
    )
  }

  const onNextPage = () => {
    setQueries(queries.updateIn(['page'], (value: any) => value + 1))
  }

  const onPreviousPage = () => {
    setQueries(queries.updateIn(['page'], (value: any) => value - 1))
  }

  const onFirstPage = () => {
    setQueries(queries.updateIn(['page'], () => 1))
  }

  const onCreateMenuItemOption = async (option: MenuOptionType) => {
    try {
      const rs = await Api.menu.createMenuOption(option, websiteId)
      window.alert('Thêm mới thành công!')
      _refresh()
      return true
    } catch (e: any) {
      console.log(e)
      window.alert(
        e?.response?.data?.error?.message ??
        'Có lỗi xảy ra trong quá trình xử lý. Vui lòng thử lại sau'
      )
      return false
    }
  }

  const deleteMenuItemOption = async (itemId: any) => {
    const itemIndex = data.findIndex((s: any) => s.get('_id') === itemId)

    if (itemIndex < 0) return

    const menuItem = data.get(itemIndex)

    setData(data.delete(itemIndex))
    try {
      const rs = await Api.menu.deleteMenuOption(itemId)
      return rs.data.data
    } catch (e: any) {
      console.log(e)
      window.alert(
        e?.response?.data?.error?.message ??
        'Có lỗi xảy ra trong quá trình xử lý. Vui lòng thử lại sau'
      )
      setData(data.insert(itemIndex, menuItem))
      return null
    }
  }

  const onEditMenuItemOption = async (optionId: string, option: MenuOptionType) => {
    try {
      await Api.menu.editMenuOption(optionId, option)
      window.alert('Chỉnh sửa thành công!')
      _refresh()
      return true
    } catch (e: any) {
      console.log(e)
      window.alert(
        e?.response?.data?.error?.message ??
        'Có lỗi xảy ra trong quá trình xử lý. Vui lòng thử lại sau'
      )
      return false
    }
  }

  React.useEffect(() => {
    if (website.get('subBrands') && data.size < 1 && !loading) {
      _initData();
    }
  }, [website.get('subBrands')]);

  React.useEffect(() => {
    _fetchWebsite()
  }, [])

  React.useEffect(() => {
    if (queries.size < 1) return
    _refresh()
  }, [queries]);

  let value = {
    queries,
    data,
    website,
    loading,
    onBrandChanged,
    deleteMenuItemOption,
    onCreateMenuItemOption,
    onEditMenuItemOption,
    onNextPage,
    onPreviousPage,
    onFirstPage,
  };

  return <WebsiteMenuItemOptionsContext.Provider value={value}>{children}</WebsiteMenuItemOptionsContext.Provider>;
}

export default WebsiteMenuItemOptionsProvider;
export { useWebsiteMenuItemOptions };